import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { KIcon } from "../../../shared/icon/k-icon";
import "./kuika-reveal-tooltip.scss";

/**
 * Shared AI tool-activity reveal tooltip.
 *
 * A stacking, auto-dismissing notification anchored to an icon — shown when the
 * AI assistant runs a tool that touches a designer surface (a datasource is
 * created, a table altered, a language added, …). It wears the "Ask mode is now
 * free" FAB-announce skin (accent gradient + soft purple shadow + spring
 * entrance) so the whole designer speaks one visual language for AI feedback.
 *
 * Placement-aware: `bottom` anchors below the icon with an up-pointing caret
 * (top toolbar), `right` anchors to the icon's right with a left-pointing caret
 * (vertical left menu).
 *
 * The component is intentionally decoupled from the MCP notification model — it
 * accepts a generic `RevealTooltipEntry`, so it can live in the ui-kit without
 * importing `shared/utility`. `BackendNotificationPayload` is structurally
 * assignable to `RevealTooltipEntry`, so callers pass their payloads directly.
 */
export interface RevealTooltipEntry {
  message: string;
  isDelete?: boolean;
  detailLines?: string[];
}

export type RevealTooltipPlacement = "bottom" | "right";

interface IKuikaRevealTooltipProps {
  /** The icon the tooltip points at. */
  anchorRef: React.RefObject<HTMLElement>;
  /**
   * Append-only feed: the latest entry is shown each time the array grows.
   * Clearing the array does NOT remove visible tooltips — each self-dismisses
   * after `DISMISS_MS`.
   */
  notifications: RevealTooltipEntry[];
  placement?: RevealTooltipPlacement;
}

interface NotificationEntry {
  id: number;
  payload: RevealTooltipEntry;
  exiting: boolean;
}

interface TooltipPosition {
  top: number;
  left: number;
  /** Caret offset along the anchored edge (px from the tooltip's edge start). */
  caretMain: number;
  isReady: boolean;
}

const MAX_VISIBLE = 3;
const DISMISS_MS = 8000;
const FADE_OUT_MS = 500;
const VIEWPORT_PADDING_PX = 8;
const TOOLTIP_OFFSET_PX = 8;
const CARET_EDGE_PADDING_PX = 16;

let nextId = 0;

const KuikaRevealTooltip: React.FC<IKuikaRevealTooltipProps> = ({ anchorRef, notifications, placement = "bottom" }) => {
  const [entries, setEntries] = useState<NotificationEntry[]>([]);
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    caretMain: 0,
    isReady: false
  });
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Append the latest entry whenever the feed grows.
  useEffect(() => {
    if (notifications.length === 0) return;

    const latest: RevealTooltipEntry = notifications[notifications.length - 1];

    setEntries((prev: NotificationEntry[]): NotificationEntry[] => {
      const entry: NotificationEntry = {
        id: ++nextId,
        payload: latest,
        exiting: false
      };
      const updated: NotificationEntry[] = [...prev, entry];
      // Trim to max visible — mark overflow as exiting.
      if (updated.length > MAX_VISIBLE) {
        const overflow: number = updated.length - MAX_VISIBLE;
        for (let i = 0; i < overflow; i++) {
          updated[i] = { ...updated[i], exiting: true };
        }
      }
      return updated;
    });
    // Intentionally keyed on length only: each growth appends the newest entry.
    // Depending on `notifications` identity would re-append on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length]);

  // Set dismiss timers for new entries.
  useEffect(() => {
    for (const entry of entries) {
      if (entry.exiting || timersRef.current.has(entry.id)) continue;

      const dismissTimer: ReturnType<typeof setTimeout> = setTimeout(() => {
        // Start fade-out.
        setEntries((prev: NotificationEntry[]): NotificationEntry[] =>
          prev.map((e: NotificationEntry): NotificationEntry => (e.id === entry.id ? { ...e, exiting: true } : e))
        );

        // Remove after the fade-out animation.
        setTimeout(() => {
          setEntries((prev: NotificationEntry[]): NotificationEntry[] =>
            prev.filter((e: NotificationEntry): boolean => e.id !== entry.id)
          );
          timersRef.current.delete(entry.id);
        }, FADE_OUT_MS);
      }, DISMISS_MS);

      timersRef.current.set(entry.id, dismissTimer);
    }
  }, [entries]);

  // Cleanup on unmount.
  useEffect(() => {
    const timers: Map<number, ReturnType<typeof setTimeout>> = timersRef.current;
    return () => {
      timers.forEach((timer: ReturnType<typeof setTimeout>) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  useLayoutEffect(() => {
    if (entries.length === 0) return;

    const updatePosition = (): void => {
      const anchorElement: HTMLElement | null = anchorRef.current;
      const tooltipElement: HTMLDivElement | null = tooltipRef.current;
      if (!anchorElement || !tooltipElement) return;

      const anchorRect: DOMRect = anchorElement.getBoundingClientRect();
      const tooltipRect: DOMRect = tooltipElement.getBoundingClientRect();

      let nextTop: number;
      let nextLeft: number;
      let nextCaretMain: number;

      if (placement === "right") {
        // To the right of the icon, vertically centered on it.
        const centeredTop: number = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2;
        const maxTop: number = Math.max(
          VIEWPORT_PADDING_PX,
          window.innerHeight - tooltipRect.height - VIEWPORT_PADDING_PX
        );
        nextTop = Math.min(maxTop, Math.max(VIEWPORT_PADDING_PX, centeredTop));
        nextLeft = anchorRect.right + TOOLTIP_OFFSET_PX;
        nextCaretMain = Math.min(
          tooltipRect.height - CARET_EDGE_PADDING_PX,
          Math.max(CARET_EDGE_PADDING_PX, anchorRect.top + anchorRect.height / 2 - nextTop)
        );
      } else {
        // Below the icon, horizontally centered on it.
        const centeredLeft: number = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
        const maxLeft: number = Math.max(
          VIEWPORT_PADDING_PX,
          window.innerWidth - tooltipRect.width - VIEWPORT_PADDING_PX
        );
        nextLeft = Math.min(maxLeft, Math.max(VIEWPORT_PADDING_PX, centeredLeft));
        nextTop = anchorRect.bottom + TOOLTIP_OFFSET_PX;
        nextCaretMain = Math.min(
          tooltipRect.width - CARET_EDGE_PADDING_PX,
          Math.max(CARET_EDGE_PADDING_PX, anchorRect.left + anchorRect.width / 2 - nextLeft)
        );
      }

      setPosition((prev: TooltipPosition): TooltipPosition => {
        if (prev.isReady && prev.top === nextTop && prev.left === nextLeft && prev.caretMain === nextCaretMain) {
          return prev;
        }
        return { top: nextTop, left: nextLeft, caretMain: nextCaretMain, isReady: true };
      });
    };

    updatePosition();

    const anchorElement: HTMLElement | null = anchorRef.current;
    const tooltipElement: HTMLDivElement | null = tooltipRef.current;
    const resizeObserver: ResizeObserver | null =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => updatePosition()) : null;

    if (resizeObserver && anchorElement) resizeObserver.observe(anchorElement);
    if (resizeObserver && tooltipElement) resizeObserver.observe(tooltipElement);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, entries, placement]);

  if (entries.length === 0) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={`kuika-reveal-tooltip kuika-reveal-tooltip--${placement}`}
      style={
        {
          top: position.top,
          left: position.left,
          visibility: position.isReady ? "visible" : "hidden",
          "--kuika-reveal-tooltip-caret-main": `${position.caretMain}px`
        } as React.CSSProperties
      }
    >
      {entries.map((entry: NotificationEntry) => (
        <div
          key={entry.id}
          className={`kuika-reveal-tooltip__item${entry.payload.isDelete ? " kuika-reveal-tooltip__item--delete" : ""}${
            entry.exiting ? " kuika-reveal-tooltip__item--exiting" : ""
          }`}
        >
          <span className="kuika-reveal-tooltip__item-icon">
            <KIcon
              iconName={entry.payload.isDelete ? "delete_outline" : "check"}
              iconType="round"
              style={{ fontSize: 16, color: "#ffffff" }}
            />
          </span>
          <div className="kuika-reveal-tooltip__item-body">
            <span className="kuika-reveal-tooltip__item-text">{entry.payload.message}</span>
            {entry.payload.detailLines && entry.payload.detailLines.length > 0 && (
              <div className="kuika-reveal-tooltip__item-details">
                {entry.payload.detailLines.map((line: string, i: number) => (
                  <span key={i} className="kuika-reveal-tooltip__detail-line">
                    {line}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

export default KuikaRevealTooltip;
